import React, { useState, useRef, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import TableHead from '../ui/TableHead';
import TableRow from '../ui/TableRow';
import TableFooter from '../ui/TableFooter';
import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';

function Table({
  isFor,
  name,
  headers,
  data,
  handleRowClick,
  isDownloadable,
  sortOptions,
  isFilterable,
  onYes,
  onNo,
  onEngineerYes,
  onEngineerNo,
  identifier,
  isDeleteEnable,
  onApprove,
  isViewEnable,
  isImportEnable,
  onImport,
  isEditEnable,
  onEdit,
  onDelete,
  onView,
  onChangeStatus,
  isChangeStatusEnable,
  bigHeaders,
  onAccept,
  isAcceptEnable,
  onCancel,
  isCancelEnable,
  isCancelDeal,
  onCancelDeal,
  onViewBrands,
  onViewItems,
  isStatusUpdateEnable,
  onStatusUpdate,
  isMarkEnable,
  onMark,
  isDollerEnable,
  onDollor,
  onViewByName,
  isViewByName,
  rowName,
  isBtnEnable,
  onBtn,
  onViewNote,
  isDownloadInvoiceEnable,
  onDownloadInvoice,
  isDownloadBillEnable,
  onDownloadBill,
  onResendInvite,
  onViewManager,
  onViewSalesExec,
  isInventoryActionEnabled,
  onInventoryPurchase,
  onInventoryUpdate,
  onDownloadDoc,
  isDownloadDocEnable,
  isDownloadSampleDocEnable,
  onDownloadSampleDoc,
  onUploadDoc,
  uploadDocId,
  onDownloadSignDoc,
  onRowClick,
  top = 0,
  isWrap = false,
  // Pagination props
  enablePagination = false,
  hasMore = false,
  loadMore,
  dataLength = 0,
  isFetching = false
}) {
  /* 
    Local state
  */
  const [query, setQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  const headerRefs = useRef([]);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (openDropdown === null) return;
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !headerRefs.current[openDropdown]?.contains(event.target)
      ) {
        setOpenDropdown(null);
        setDropdownRect(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  /* 
    Computation
  */
  const width = 100 / headers.length >= 25 ? 100 / headers.length - 1 : 100 / headers.length;

  function handleQueryChange(e) {
    const targetValue = e.target.value;
    setQuery(targetValue);
  }

  // Helper to get header label
  const getHeaderLabel = (header) => (typeof header === 'string' ? header : header.label);

  return (
    <div className="w-full h-full bg-white border-[#E7EBF1] border mb-4 rounded-xl relative">
      <div role="table" className="table-container h-full" style={{ position: 'relative' }}>
        <div
          className="w-full justify-between bg-[#F8FAFC] p-3 flex border-b border-[#E7EBF1] text-[#64748B] text-sm font-semibold rounded-t-xl"
          style={{ position: 'sticky', top: top, left: 0, zIndex: 1 }}>
          {headers?.map((header, idx) => {
            const isDropdown = typeof header === 'object' && header.dropdown;
            return (
              <div
                key={getHeaderLabel(header)}
                className="relative flex items-center justify-start gap-x-1"
                style={{ width: `${width}%`, minWidth: isDropdown ? '150px' : 'auto' }}
                ref={(el) => (headerRefs.current[idx] = el)}>
                {isDropdown ? (
                  <>
                    <div className="flex justify-start px-3">{getHeaderLabel(header)}</div>
                    <ChevronDown
                      className="cursor-pointer flex-shrink-0"
                      size={16}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (openDropdown === idx) {
                          setOpenDropdown(null);
                          setDropdownRect(null);
                        } else {
                          setOpenDropdown(idx);
                          const rect = headerRefs.current[idx]?.getBoundingClientRect();
                          setDropdownRect(rect);
                        }
                      }}
                    />
                    {openDropdown === idx &&
                      dropdownRect &&
                      createPortal(
                        <div
                          ref={dropdownRef}
                          style={{
                            position: 'fixed',
                            left: dropdownRect.left,
                            top: dropdownRect.bottom,
                            minWidth: Math.max(dropdownRect.width, 200),
                            zIndex: 9999,
                            background: 'white',
                            borderRadius: '0.375rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            border: '1px solid #e5e7eb',
                            maxHeight: '250px',
                            overflowY: 'auto'
                          }}
                          onClick={(e) => e.stopPropagation()}>
                          {header.dropdownOptions?.map((opt) => (
                            <div
                              key={opt.id}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                header.selectedValue === opt.id ? 'bg-blue-100 font-medium' : ''
                              }`}
                              onClick={() => {
                                setOpenDropdown(null);
                                setDropdownRect(null);
                                header.onDropdownSelect && header.onDropdownSelect(opt.id);
                              }}>
                              {opt.label}
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                  </>
                ) : (
                  <TableHead name={getHeaderLabel(header)} width={width} isWrap={isWrap} />
                )}
              </div>
            );
          })}
        </div>

        {enablePagination ? (
          <div
            className={`md:block absolute top-15 bg-white w-full h-full table-body rounded-b-lg ${bigHeaders && 'mt-[2rem]'}`}
            id="scrollableTableDiv"
            style={{ overflowY: 'auto', maxHeight: '100%', paddingBottom: '3.2rem' }}>
            <InfiniteScroll
              dataLength={dataLength}
              next={() => {
                console.log('🔄 Table InfiniteScroll next function called');
                loadMore && loadMore();
              }}
              hasMore={hasMore}
              loader={
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading more data...</span>
                </div>
              }
              endMessage={
                dataLength > 0 && (
                  <div className="flex justify-center items-center py-4">
                    <p className="text-gray-500">No more data to load</p>
                  </div>
                )
              }
              scrollableTarget="scrollableTableDiv"
              scrollThreshold={0.9}
              style={{ overflow: 'visible' }}>
              {data && data.length > 0 ? (
                data?.map((item, idx) => (
                  <TableRow
                    key={item?.[identifier] ?? idx}
                    data={item}
                    width={width}
                    identifier={identifier}
                    handleRowClick={onRowClick || handleRowClick}
                    isDeleteEnable={isDeleteEnable}
                    isViewEnable={isViewEnable}
                    isImportEnable={isImportEnable}
                    onImport={onImport}
                    isEditEnable={isEditEnable}
                    onMark={onMark}
                    onYes={onYes}
                    onNo={onNo}
                    onEngineerYes={onEngineerYes}
                    onEngineerNo={onEngineerNo}
                    onApprove={onApprove}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    isAcceptEnable={isAcceptEnable}
                    onAccept={onAccept}
                    onChangeStatus={onChangeStatus}
                    isChangeStatusEnable={isChangeStatusEnable}
                    isCancelEnable={isCancelEnable}
                    isCancelDeal={isCancelDeal}
                    onCancelDeal={onCancelDeal}
                    onCancel={onCancel}
                    onViewBrands={onViewBrands}
                    onViewItems={onViewItems}
                    isStatusUpdateEnable={isStatusUpdateEnable}
                    onStatusUpdate={onStatusUpdate}
                    isMarkEnable={isMarkEnable}
                    isDollerEnable={isDollerEnable}
                    onDollor={onDollor}
                    onViewByName={onViewByName}
                    isViewByName={isViewByName}
                    rowName={rowName}
                    isBtnEnable={isBtnEnable}
                    onBtn={onBtn}
                    onViewNote={onViewNote}
                    isDownloadInvoiceEnable={isDownloadInvoiceEnable}
                    onDownloadInvoice={onDownloadInvoice}
                    isDownloadBillEnable={isDownloadBillEnable}
                    onDownloadBill={onDownloadBill}
                    onResendInvite={onResendInvite}
                    onViewManager={onViewManager}
                    onViewSalesExec={onViewSalesExec}
                    isInventoryActionEnabled={isInventoryActionEnabled}
                    onInventoryPurchase={onInventoryPurchase}
                    onInventoryUpdate={onInventoryUpdate}
                    onDownloadDoc={onDownloadDoc}
                    isDownloadDocEnable={isDownloadDocEnable}
                    onUploadDoc={onUploadDoc}
                    uploadDocId={uploadDocId}
                    onDownloadSignDoc={onDownloadSignDoc}
                    isDownloadSampleDocEnable={isDownloadSampleDocEnable}
                    onDownloadSampleDoc={onDownloadSampleDoc}
                    className="hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                  />
                ))
              ) : (
                <div className="flex justify-center items-center h-40">
                  <p className="text-gray-400">No data found</p>
                </div>
              )}
            </InfiniteScroll>
          </div>
        ) : (
          <div
            className={`md:block absolute top-15 border-b border-[#EEF2F6] w-full h-full table-body rounded-b-lg ${bigHeaders && 'mt-[2rem]'}`}
            style={{ overflowY: 'auto', maxHeight: '100%', paddingBottom: '3.2rem' }}>
            {data && data.length > 0 ? (
              data?.map((item, idx) => (
                <TableRow
                  key={item?.[identifier] ?? idx}
                  data={item}
                  width={width}
                  identifier={identifier}
                  handleRowClick={onRowClick || handleRowClick}
                  isDeleteEnable={isDeleteEnable}
                  isViewEnable={isViewEnable}
                  isImportEnable={isImportEnable}
                  onImport={onImport}
                  isEditEnable={isEditEnable}
                  onMark={onMark}
                  onYes={onYes}
                  onNo={onNo}
                  onEngineerYes={onEngineerYes}
                  onEngineerNo={onEngineerNo}
                  onApprove={onApprove}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isAcceptEnable={isAcceptEnable}
                  onAccept={onAccept}
                  onChangeStatus={onChangeStatus}
                  isChangeStatusEnable={isChangeStatusEnable}
                  isCancelEnable={isCancelEnable}
                  isCancelDeal={isCancelDeal}
                  onCancelDeal={onCancelDeal}
                  onCancel={onCancel}
                  onViewBrands={onViewBrands}
                  onViewItems={onViewItems}
                  isStatusUpdateEnable={isStatusUpdateEnable}
                  onStatusUpdate={onStatusUpdate}
                  isMarkEnable={isMarkEnable}
                  isDollerEnable={isDollerEnable}
                  onDollor={onDollor}
                  onViewByName={onViewByName}
                  isViewByName={isViewByName}
                  rowName={rowName}
                  isBtnEnable={isBtnEnable}
                  onBtn={onBtn}
                  onViewNote={onViewNote}
                  isDownloadInvoiceEnable={isDownloadInvoiceEnable}
                  onDownloadInvoice={onDownloadInvoice}
                  isDownloadBillEnable={isDownloadBillEnable}
                  onDownloadBill={onDownloadBill}
                  onResendInvite={onResendInvite}
                  onViewManager={onViewManager}
                  onViewSalesExec={onViewSalesExec}
                  isInventoryActionEnabled={isInventoryActionEnabled}
                  onInventoryPurchase={onInventoryPurchase}
                  onInventoryUpdate={onInventoryUpdate}
                  onDownloadDoc={onDownloadDoc}
                  isDownloadDocEnable={isDownloadDocEnable}
                  onUploadDoc={onUploadDoc}
                  uploadDocId={uploadDocId}
                  onDownloadSignDoc={onDownloadSignDoc}
                  isDownloadSampleDocEnable={isDownloadSampleDocEnable}
                  onDownloadSampleDoc={onDownloadSampleDoc}
                  className="hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                />
              ))
            ) : (
              <div className="flex justify-center items-center h-40">
                <p className="text-gray-400">No data found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Table;
